import { BehaviorSubject, Observable } from 'rxjs';
import { Role, UserInfo } from '../models';
import { Inject, Injectable } from '@angular/core';
import {
  NullValidationHandler,
  OAuthEvent,
  OAuthService,
} from 'angular-oauth2-oidc';
import { filter, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { getAuth } from '../config/auth.config';
import { ENVIRONMENT_CONFIG } from '../config/environment.config.token';
import { EnvironmentConfig } from '../config/environment.config';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserInfo>(null);
  public onIdentityPersonSubject = new BehaviorSubject<boolean>(false);
  public currentUser$: Observable<UserInfo>;
  public get currentUser(): UserInfo {
    return this.currentUserSubject.getValue();
  }
  public rootPermissionLoaded$ = new BehaviorSubject<boolean>(false);
  constructor(
    private oauthService: OAuthService,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig,
    private router: Router,
    public permissionsService: NgxPermissionsService,
    public userService: UserService
  ) {
    this.currentUser$ = this.currentUserSubject.asObservable().pipe(
      filter((user) => !!user),
      tap((user) => this.setCurrentUser(user))
    );
  }

  async configure(): Promise<any> {
    const authConfig = getAuth(this.config.env);
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events.subscribe(({ type }: OAuthEvent) => {
      switch (type) {
        case 'token_received': {
          try {
            this.handleLoggedIn();
            var redirectUrl = decodeURIComponent(this.oauthService.state);
            if (
              redirectUrl.startsWith('/invites') ||
              redirectUrl.startsWith('/doctor/profile')
            ) {
              this.router.navigate([redirectUrl]);
            }
          } catch (err) {
            console.error(err);
          }
        }
      }
    });

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    var isValidToken = this.hasValidToken();
    if (isValidToken) {
      this.handleLoggedIn();
    }
    return isValidToken;
  }

  async handleLoggedIn() {
    console.log('Logged in start...');
    const claims: any = this.oauthService.getIdentityClaims();
    const user = this.mapClaimsToUser(claims);
    await this.loadPermissions(claims);
    this.currentUserSubject.next(user);
    this.userService.getTenantRootPermissions().subscribe({
      next: (a) => {
        if (Array.isArray(a)) {
          this.permissionsService.addPermission(a as any[]);
          this.rootPermissionLoaded$.next(true); //reload menu
        } else {
          this.rootPermissionLoaded$.next(true);
        }
      },
      error: () => {},
      complete: () => {},
    });
  }

  login(additionalState?: string, params = {}) {
    this.oauthService.initLoginFlow(additionalState, params);
  }

  loginOneId(returnUrl?: string, params?: {}) {
    params = { ...params, acr_values: 'idp:OneId' };
    this.oauthService.initLoginFlow(returnUrl, params);
  }

  loadUserProfile() {
    return this.oauthService.loadUserProfile();
  }

  refresh() {
    return this.oauthService.refreshToken();
  }
  refreshWithLoadPermissions(): void {
    this.oauthService.refreshToken().then((s) => {
      this.handleLoggedIn();
    });
  }
  hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  hasValidAccessToken() {
    return this.oauthService.hasValidAccessToken();
  }

  getIdentityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  logout() {
    this.oauthService.logOut();
  }

  hasRole(role: Role) {
    return this.currentUserSubject.value.role.includes(role);
  }

  hasPermission(permission: string | string[]) {
    if (this.currentUserIsSuperAdmin()) {
      return Promise.resolve(true);
    }
    if (!permission) return Promise.resolve(false);

    return this.permissionsService.hasPermission(permission);
  }

  currentUserIsSuperAdmin(): boolean {
    const user = this.currentUserSubject.value;
    return user && Array.isArray(user.role) && user.role.includes(Role.Admin);
  }
  private setCurrentUser(user: UserInfo) {
    localStorage.setItem('current-user', JSON.stringify(user));
  }

  private mapClaimsToUser(claims: any): UserInfo {
    return {
      id: claims.sub,
      userName: claims.name,
      firstName: claims.firstName || claims.name,
      lastName: claims.lastName || '',
      role: typeof claims.role === 'string' ? [claims.role] : claims.role,
      avatarUrl: claims.avatarUrl,
      personId: claims.personId,
      clinicId: claims.cid,
      tenantId: claims.tid,
      warehouseId: claims.warehouseId,
      employeeId: claims.employeeId,
      positionId: claims.positionId,
      screenId: claims.screenId,
      registratorId: claims.registratorId,
      executorId: claims.executorId,
      departmentId: claims.departmentId,
    };
  }

  private async loadPermissions(claims: any) {
    this.permissionsService.flushPermissions();

    if (!claims.permission) return;

    const permissions = Array.isArray(claims.permission)
      ? claims.permission
      : [claims.permission];

    await this.permissionsService.loadPermissions(permissions);
  }
}
